import { Fragment, useContext, useState } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { toast, DotLoader } from '@opallabs/lapidary'
import { ContentCreatedToast } from '../../../../Toasts'
import type { PartialStory, ServiceWithSystemName } from '../../../momentTypes'
import { useCreateContent } from '../../../queries/useCreateContent'
import type { ServiceOption } from '../../../queries/useSystemServices'
import { WeekViewGlobalContext } from '../../../weekViewGlobalContext'
import { ContextMenuCallbacks, Moment } from '../momentCardTypes'
import MomentServices from '../MomentServices'
import { AddContentPopover } from './AddContentPopover'
import ContentSummary from './ContentSummary'
import ExpandedContent from './ExpandedContent'
import { styles } from './styles'

type MomentCardFooterProps = {
  activeFilters: string[]
  selectedFilters?: Record<string, unknown>
  collapseContent: boolean
  contextMenuCallbacks?: ContextMenuCallbacks
  isContentDropzoneActive: boolean
  isMomentDragActive?: boolean
  isSelected?: boolean
  moment?: Moment
  momentCardRef: React.MutableRefObject<HTMLDivElement | null>
  onDragRestrictedContent?: () => void
  services?: Array<ServiceWithSystemName>
  story?: PartialStory
}

export const MomentCardFooter: React.FC<MomentCardFooterProps> = ({
  activeFilters,
  selectedFilters,
  collapseContent,
  contextMenuCallbacks,
  isContentDropzoneActive,
  isMomentDragActive,
  isSelected,
  moment,
  momentCardRef,
  onDragRestrictedContent,
  story,
}) => {
  const [showCollapsedAddButton, setShowCollapsedAddButton] = useState(false)
  const [collapsedPopoverOpen, setcollapsedPopoverOpen] = useState(false)
  const { loadUpdatedJsonApiEntities, refetchMoments, setDisableActiveColumn } =
    useContext(WeekViewGlobalContext)

  const createContent = useCreateContent()
  const onCreateContent = async (selectedServices: Array<ServiceOption>) => {
    if (!moment) return
    // `mutateAsync`, not `mutate`: a per-call `onSuccess` fires only for the
    // latest call, so with several channels the earlier creates would be lost.
    const results = await Promise.allSettled(
      selectedServices.map((service) =>
        createContent
          .mutateAsync({ serviceId: service.id, momentId: moment.id })
          .then((data) => {
            toast(
              (props) => (
                <ContentCreatedToast
                  {...props}
                  selectedServices={selectedServices}
                />
              ),
              {
                type: 'success',
                toastId: 'success-content-added-to-moment',
                closeOnClick: true,
              }
            )
            /** Deserialize content response into redux. */
            if (typeof loadUpdatedJsonApiEntities === 'function') {
              loadUpdatedJsonApiEntities(data)
            }
          })
      )
    )
    // Creating content leaves the moment's `updated_at` unchanged, so
    // `loadUpdatedJsonApiEntities` keeps the stored moment and its old content
    // list. Only a refetch brings in the new content (#16447).
    if (results.some((result) => result.status === 'fulfilled')) {
      refetchMoments()
    }
  }

  if (!moment || (moment && !moment.postCount)) {
    return (
      <div
        css={styles.cardFooter}
        className={kuiperClassNames('emptyMomentCard-footer')}
      >
        {createContent.isLoading ? (
          <div className={kuiperClassNames('loader-wrapper')}>
            <DotLoader />
          </div>
        ) : (
          <AddContentPopover
            collapseButton={collapseContent}
            onCreateContent={onCreateContent}
            onClosePopover={() => setDisableActiveColumn(false)}
            onOpenPopover={() => setDisableActiveColumn(true)}
          />
        )}
        {!collapseContent && (
          <div
            className={kuiperClassNames('contentPlaceholder', {
              isContentDropzoneActive,
            })}
          />
        )}
      </div>
    )
  }

  const onOpenCollapsedPopover = () => {
    setDisableActiveColumn(true)
    setcollapsedPopoverOpen(true)
  }

  const onCloseCollapsedPopover = (byKeypress?: boolean) => {
    if (!byKeypress) {
      setShowCollapsedAddButton(false)
    }
    setDisableActiveColumn(false)
    setcollapsedPopoverOpen(false)
  }

  return (
    <div
      css={styles.footerWrapper}
      onMouseEnter={() =>
        !collapsedPopoverOpen && setShowCollapsedAddButton(true)
      }
      onMouseLeave={() =>
        !collapsedPopoverOpen && setShowCollapsedAddButton(false)
      }
    >
      {!collapseContent && <ContentSummary moment={moment} />}
      {!collapseContent ? (
        <ExpandedContent
          activeFilters={activeFilters}
          selectedFilters={selectedFilters}
          contextMenuCallbacks={contextMenuCallbacks}
          isMomentDragActive={isMomentDragActive}
          isSelected={isSelected}
          moment={moment}
          onDragRestrictedContent={onDragRestrictedContent}
          story={story}
        />
      ) : (
        <div className={kuiperClassNames('momentCard-contentListCollapsed')}>
          {createContent.isLoading ? (
            <DotLoader height={24} />
          ) : (
            <Fragment>
              <MomentServices
                momentCardRef={momentCardRef}
                onFocusFirstItem={() => setShowCollapsedAddButton(true)}
                services={moment?.posts.map((post) => post.service)}
              />
              {showCollapsedAddButton && (
                <AddContentPopover
                  collapseButton={true}
                  onBlurButton={() =>
                    !collapsedPopoverOpen && setShowCollapsedAddButton(false)
                  }
                  onCreateContent={onCreateContent}
                  onClosePopover={onCloseCollapsedPopover}
                  onOpenPopover={onOpenCollapsedPopover}
                />
              )}
            </Fragment>
          )}
        </div>
      )}
    </div>
  )
}
